import { GripVerticalIcon, XIcon } from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getCurrencySymbol } from "../shared/currency";
import type { BrowseFilter, Platform, SelectedTalent, Talent, User } from "../shared/types";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Overline, TextLink } from "@/components/typography";
import { LogoIcon } from "./LogoIcon";

export interface SortableTalentMiniProps {
    talId: string;
    talent: Talent[];
    platforms: Platform[];
    filter: Pick<BrowseFilter, "campaignType" | "currency">;
    selectedTalent: SelectedTalent;
    notesBookerViewable?: boolean;
    notesRequired?: boolean;
    useCustomPrices?: boolean;
    listUid?: string;
    user: User | null | undefined;
    canReorder?: boolean;
    onNotesChange: (id: string, notes: string) => void;
    onRemove: (id: string) => void;
}

export default function SortableTalentMini({
    talId,
    talent,
    platforms,
    filter,
    selectedTalent,
    notesBookerViewable,
    notesRequired,
    useCustomPrices,
    listUid,
    user,
    canReorder = false,
    onNotesChange,
    onRemove,
}: SortableTalentMiniProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: talId, disabled: !canReorder });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const tal = talent.find((t) => t.id === talId);
    const canRemove = !!(
        user?.roles?.admin
        || user?.id === listUid
        || (user?.id && tal?.managerIds?.includes(user.id))
    );

    return (
        // Row: optional drag handle, avatar + notes, remove control
        <div className="mb-4 flex justify-between text-sm" ref={setNodeRef} style={style}>
            {canReorder ? (
                <Button
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    aria-label="Reorder creator"
                    className="cursor-grab touch-none self-start"
                    {...attributes}
                    {...listeners}
                >
                    <GripVerticalIcon />
                </Button>
            ) : null}
            <div className="flex flex-1 items-start">
                <div
                    className="mr-4 size-12 shrink-0 rounded bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${tal?.imgUrl ? tal.imgUrl : "talent-default.png"})` }}
                />
                <div className="flex-1">
                    <div>
                        <strong>
                            {platforms.map((platform) => (
                                platform === "TikTok" ? (
                                    <TextLink key="TikTok" variant="plain" className="mr-2 inline-flex items-center" href={`https://tiktok.com/@${tal?.platformData?.TikTok?.username?.toLowerCase()}`} target="_blank" rel="noreferrer">
                                        <LogoIcon brand="TikTok" className="mr-2 size-4" />
                                        @{tal?.platformData?.TikTok?.username}
                                    </TextLink>
                                ) : platform === "Instagram" ? (
                                    <TextLink key="Instagram" variant="plain" className="mr-2 inline-flex items-center" href={`https://instagram.com/${tal?.platformData?.Instagram?.username}`} target="_blank" rel="noreferrer">
                                        <LogoIcon brand="Instagram" className="mr-2 size-4" />
                                        @{tal?.platformData?.Instagram?.username}
                                    </TextLink>
                                ) : (
                                    <TextLink key="YouTube" variant="plain" className="mr-2 inline-flex items-center" href={"https://www.youtube.com/@" + tal?.platformData?.YouTube?.username} target="_blank" rel="noreferrer">
                                        <LogoIcon brand="YouTube" className="mr-2 size-4" />
                                        {tal?.platformData?.YouTube?.username}
                                    </TextLink>
                                )
                            ))}
                        </strong>

                        {(useCustomPrices && (
                            <> &nbsp;- {getCurrencySymbol(filter.currency)}{selectedTalent[talId].price}</>
                        ))
                            || (filter.campaignType === "Song" && (
                                <> &nbsp;- {getCurrencySymbol(filter.currency)}{talent.find((t) => t.id === talId)?.defaultPrice} (Default Price)</>
                            ))
                            || null}

                        <div className="mt-2">
                            <Overline className="mb-1">{notesBookerViewable ? "Booker Viewable" : "Internal"} Notes:</Overline>
                            <Textarea
                                required={!!notesRequired}
                                rows={2}
                                className="min-h-0 resize-y text-sm"
                                value={selectedTalent[talId].notes}
                                onChange={(e) => onNotesChange(talId, e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="ml-1">
                {canRemove ? (
                    <Button type="button" size="icon-sm" variant="ghost" onClick={() => onRemove(talId)} aria-label="remove">
                        <XIcon />
                    </Button>
                ) : null}
            </div>
        </div>
    );
}
